
import { mapGetters, mapActions } from 'vuex'
import moment from 'moment'
import numbro from 'numbro'

import TextBoxMetric from '@/components/TextBoxMetric.vue'
import MarketingSpend from '@/components/simulationDetails/MarketingSpend.vue'
import MarketingSpendDetail from '@/components/simulationDetails/MarketingSpendDetail.vue'
import SalesDecomposition from '@/components/simulationDetails/SalesDecomposition.vue'
import MarketingSalesByMicroaudience from '@/components/simulationDetails/MarketingSalesByMicroaudience.vue'
import MarketingSales from '@/components/simulationDetails/MarketingSales.vue'
import MarketingSalesDetail from '@/components/simulationDetails/MarketingSalesDetail.vue'
import MarketingSpendSegmentsDetail from '@/components/simulationDetails/MarketingSpendSegmentsDetail.vue'
import RoasByChannel from '@/components/simulationDetails/RoasByChannel.vue'
import SaturationCurves from '@/components/SaturationCurves.vue'
import PercentChip from '@/components/PercentChip.vue'
import BarChart from '@/components/BarChart.vue'
import PeriodicDetail from '@/components/PeriodicDetail.vue'
import HiddenSectionsReport from '@/components/HiddenSectionsReport.vue'
import NgtorchRunChip from '@/components/NgtorchRunChip.vue'
import { prettyNames } from '@/mixins/prettyNames.js'
import { ngtorchRuns } from '@/mixins/ngtorchRuns.js'
import { loadModelReportFromClient } from '@/utils/modelReport.js'
import { isSpendUpTo, investedBudget } from '@/utils/optimizerBudget.js'
import { roasUnitLabel } from '@/utils/roasUnits.js'
import { hiddenSaturationSections } from '@/utils/saturationSections.js'
import { interpolateCurveValue } from '@/utils/saturationDatapoints.js'
import { PERIODIC_DETAIL_SECTIONS, periodicDetailVisibility, resolvePeriodicSales } from '@/utils/periodicDetailSections.js'
import { defaultComponentDictionary, mergeComponentDictionary } from '@/utils/componentDictionary.js'
import {
  seriesMapToRows,
  componentsToRows,
  objectToRows,
  saturationCurvesToRows,
  saturationDatapointsToRows,
  segmentDistributionsToRows,
  namesValuesToRows,
  buildPerKpi,
  downloadPerKpi
} from '@/utils/modelReportXlsx.js'
import { copyTextToClipboard } from '@/utils/clipboard.js'
import { hiddenKpis, hasHiddenKpis, isKpiHidden, firstVisibleOutputName } from '@/utils/hiddenKpis.js'

export default {
  data () {
    return {
      simulation: null,
      client: null,
      currentTab: 0,
      currentOutput: null,
      dialogSharePlan: false,
      sharePlan: false,
      sharePlanLink: '',
      sharePlanId: '',
      snackbarCopySharePlanLinkToClipboard: false,
      downloadingXlsx: false,
      downloadXlsxFailed: false,
      fetchedModelReport: null,
      fetchedModelReportFor: null
    }
  },
  mixins: [prettyNames, ngtorchRuns],
  methods: {
    ...mapActions('clients', [
      'setCurrentClient'
    ]),
    ...mapActions('simulations', [
      'loadSimulations'
    ]),
    ...mapActions('shared', [
      'getSharePlanLink',
      'deleteSharePlanLink'
    ]),
    async toggleSharePlan () {
      if (this.sharePlan) {
        // Get link
        const result = await this.getSharePlanLink({
          clientId: this.clientId.code,
          planId: this.simulationId.id,
          uid: this.authUser.uid,
          hostname: window.location.hostname,
          generate: true
        })
        this.sharePlanLink = result.link
        this.sharePlanId = result.shareId
      } else {
        this.sharePlanLink = ''
        this.deleteSharePlanLink({
          shareId: this.sharePlanId
        })
        this.sharePlanId = ''
      }
    },
    async openDialogSharePlan () {
      // Get link if it exists (don't generate, if it doesn't)
      const result = await this.getSharePlanLink({
        clientId: this.clientId.code,
        planId: this.simulationId.id,
        hostname: window.location.hostname,
        generate: false
      })
      if (result && result.link && result.link.length > 0) {
        this.sharePlanLink = result.link
        this.sharePlanId = result.shareId
        this.sharePlan = true
      } else {
        this.sharePlanLink = ''
        this.sharePlanId = ''
        this.sharePlan = false
      }
      this.dialogSharePlan = true
    },
    copySharePlanLinkToClipboard () {
      copyTextToClipboard(this.sharePlanLink)
        .then(copied => {
          if (copied) {
            this.snackbarCopySharePlanLinkToClipboard = true
          } else {
            console.error('Failed to copy link')
          }
        })
    },
    formatDate (date) {
      return moment(date).format('ll')
    },
    formatValue: function (value) {
      return numbro(value).format({
        average: true,
        lowPrecision: false,
        mantissa: 1,
        optionalMantissa: true
      })
    },
    injectChanges () {
      try {
        this.data = JSON.parse(this.$refs['raw_data'].internalValue)
        this.simulation.budget = this.data.invest.budget
      } catch (error) {
        console.log(error)
      }
    },
    // XLSX download: the whole plan, one sheet per section, in one workbook per KPI of the
    // selector (a ZIP of them when there is more than one). SheetJS is loaded only on click so it
    // stays out of the route's bundle.
    async downloadPlanXlsx () {
      if (this.downloadingXlsx) return
      this.downloadingXlsx = true
      try {
        const kpis = this.outputNames.length > 1 ? this.outputNames : [this.currentOutput]
        const books = buildPerKpi(kpis, this.currentOutput, kpi => { this.currentOutput = kpi }, () => this.buildXlsxSheets())
        const XLSX = await import('xlsx')
        const period = [this.simulation.date_from, this.simulation.date_to].filter(Boolean).join('_')
        const name = `[${this.planTypeName}] ${this.simulation.name}`
        downloadPerKpi(XLSX, books, kpi => [this.client.name, name, kpi, period].filter(Boolean).join(' - '))
      } catch (error) {
        console.error('Failed to download the plan as XLSX:', error)
        this.downloadXlsxFailed = true
      } finally {
        this.downloadingXlsx = false
      }
    },
    // The plan's sections as [{ title, suffix, rows }], in the order the page shows them. A
    // section goes in when the page shows it (the same show* computeds and periodic-detail rules
    // the template reads), whichever tab is open. Sections without data are left out.
    buildXlsxSheets () {
      const t = (key, params) => this.$t('components.SimulationDetailsComponent.' + key, params || {})
      const tx = (key, params) => this.$t('components.ModelReportComponent.' + key, params || {})
      const data = this.data || {}
      const invest = this.investData
      const output = this.currentOutputObject || {}
      const kpi = this.customKpiName
      const kpiHeader = `${kpi} (${this.currentOutputUnitName})`
      const spendHeader = `${tx('spend')} (${this.spendUnitName})`
      const dateHeader = tx('xlsxDate')
      const channelHeader = tx('xlsxChannel')
      const componentsDict = this.client.pretty_names && this.client.pretty_names.components ? this.client.pretty_names.components : {}
      const decompositionDictionary = mergeComponentDictionary(defaultComponentDictionary(key => this.$t(key)), componentsDict)
      const componentName = name => decompositionDictionary[name] || name
      // Same labels as the Model Report: the Instant Marketing group is always "Marketing".
      const groupLabel = group => (group === 'Instant Marketing' ? 'Marketing' : componentName(group))

      const sheets = []
      const add = (title, suffix, rows) => {
        if (rows) sheets.push({ title: title, suffix: suffix, rows: rows })
      }

      // Summary
      add(tx('summary'), null, [
        [tx('xlsxMetric'), tx('xlsxValue')],
        [tx('xlsxModel'), this.client.name],
        [t('xlsxPlan'), this.simulation.name],
        [t('kpi'), this.currentOutput || kpi],
        [tx('xlsxStartDate'), this.simulation.date_from],
        [tx('xlsxEndDate'), this.simulation.date_to],
        [`${t('budget')} (${this.spendUnitName})`, this.budgetDisplay.value],
        [`${t('predictedSales', { kpi: kpi })} (${this.currentOutputUnitName})`, this.sales]
      ])
      if (this.showRestrictionsChip) {
        const rows = [[channelHeader, t('restrictions'), t('xlsxFirstValue'), t('xlsxSecondValue')]]
        Object.keys(this.simulation.restrictions).forEach(channel => {
          const r = this.simulation.restrictions[channel] || {}
          rows.push([
            this.prettyMediaNamesDict[channel] || channel,
            this.restrictionTypeName(r.type) || r.type,
            r.type === 'unrestricted' ? null : r.firstValue,
            r.type === 'between' ? r.secondValue : null
          ])
        })
        add(t('restrictions'), null, rows)
      }

      // Full Period Summary
      let decomposition = null
      if (this.salesDecompositionByPeriodData) {
        decomposition = this.salesDecompositionByPeriodData.components.map(c => ({ name: componentName(c.name), value: c.value }))
      } else if (output.components && typeof output.components === 'object') {
        decomposition = Array.isArray(output.components)
          ? output.components.map(c => ({ name: componentName(c.name), value: c.value }))
          : Object.keys(output.components).map(name => ({ name: componentName(name), value: output.components[name] }))
        // Same order as the chart's legacy branch: descending absolute value.
        decomposition.sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
      }
      add(this.$t('components.simulationDetails.SalesDecomposition.salesDecomposition', { kpi: kpi }), null,
        componentsToRows(decomposition, tx('xlsxComponent'), kpiHeader, this.$t('components.simulationDetails.SalesDecomposition.total'), this.sales))
      const marketing = this.mediaAttributedDecompositionByPeriodData
        ? this.mediaAttributedDecompositionByPeriodData.marketing
        : output.marketing
      if (marketing) {
        const byChannel = {}
        Object.keys(marketing).forEach(channel => {
          if (marketing[channel]) byChannel[channel] = marketing[channel].adstock
        })
        add(this.$t('components.simulationDetails.MarketingSales.mediaSalesByChannel', { kpi: kpi }), null,
          componentsToRows(byChannel, channelHeader, kpiHeader))
      }
      add(t('mediaSpendCalendar'), null, objectToRows(invest.time, [dateHeader, spendHeader]))
      if (invest.mix) {
        const rows = [[channelHeader, spendHeader, t('xlsxShareOfBudget')]]
        Object.keys(invest.mix).forEach(channel => {
          const spend = invest.mix[channel] ? invest.mix[channel].invest : null
          rows.push([channel, spend, (invest.budget && typeof spend === 'number') ? spend / invest.budget : null])
        })
        add(this.$t('components.simulationDetails.MarketingSpend.mediaMix'), null, rows.length > 1 ? rows : null)
      }
      if (this.showRoasByChannel) {
        const roasLabel = key => this.$t('components.simulationDetails.RoasByChannel.' + key) + (this.roasUnit ? ` (${this.roasUnit})` : '')
        const rows = [[channelHeader, roasLabel('mean'), roasLabel('lower'), roasLabel('upper')]]
        Object.keys(this.roasByChannelData).forEach(channel => {
          const r = this.roasByChannelData[channel] || {}
          rows.push([channel, r.mean, r.lower, r.upper])
        })
        add(this.$t('components.simulationDetails.RoasByChannel.title'), null, rows)
      }
      if (this.showSaturationCurves) {
        const saturationTitle = this.$t('components.SaturationCurves.saturationCurves')
        add(saturationTitle, null, saturationCurvesToRows(this.planSaturationData, groupLabel,
          [tx('xlsxGroup'), channelHeader, spendHeader, kpiHeader, tx('xlsxMarginalRoas')]))
        add(saturationTitle, tx('xlsxDatapoints'), saturationDatapointsToRows(this.planSaturationData, groupLabel,
          [tx('xlsxGroup'), channelHeader, dateHeader, spendHeader, kpiHeader]))
      }

      // Monthly and Weekly Detail Views: one sheet per chart, named by granularity when the plan
      // has both views.
      const periodicViews = []
      if (this.showMonthlyDetailTab) periodicViews.push({ data: data, label: tx('monthly') })
      if (this.showWeeklyDetailTab) periodicViews.push({ data: data.weekly_detail, label: tx('weekly') })
      const externalDict = this.client.pretty_names && this.client.pretty_names.externalVariables ? this.client.pretty_names.externalVariables : {}
      const seriesSheet = (map, dict) => seriesMapToRows(map, dateHeader,
        map ? Object.keys(map).map(key => ({ key: key, label: dict[key] || key })) : null)
      const pd = (key, params) => this.$t('components.PeriodicDetail.' + key, params || {})
      periodicViews.forEach(view => {
        const suffix = periodicViews.length > 1 ? view.label : null
        const visible = periodicDetailVisibility(view.data, this.currentOutput)
        const sales = resolvePeriodicSales(view.data, this.currentOutput)
        if (visible.budgetAllocation) add(pd('budgetAllocationAndMediaMix'), suffix, seriesSheet(view.data.invest.media_spend, {}))
        if (visible.kpiByPeriod) add(kpi, suffix, objectToRows(sales.time, [dateHeader, kpiHeader]))
        if (visible.salesDecomposition) add(pd('salesDecomposition', { kpi: kpi }), suffix, seriesSheet(sales.sales_decomposition_by_period, decompositionDictionary))
        if (visible.mediaEffect) add(pd('mediaEffect'), suffix, seriesSheet(sales.media_attributed_sales_decomposition_by_period, {}))
        if (visible.competitionEffect) add(pd('competitionEffect'), suffix, seriesSheet(sales.competition_effect, externalDict))
        if (visible.economyAndOthersEffect) add(pd('economyAndOthersEffect'), suffix, seriesSheet(sales.economy_and_others_effect, externalDict))
        if (visible.pricingEffect) add(pd('pricingEffect'), suffix, seriesSheet(sales.pricing_effect, externalDict))
        if (visible.distributionEffect) add(pd('distributionEffect'), suffix, seriesSheet(sales.distribution_effect, externalDict))
        if (visible.seasonalityAndOutliersEffect) add(pd('seasonalityAndOutliersEffect'), suffix, seriesSheet(sales.seasonality_and_outliers_effect, externalDict))
      })

      // Touchpoints
      if (this.showTouchpointsTab) {
        const rows = [[channelHeader, t('xlsxTouchpoint'), spendHeader]]
        Object.keys(invest.mix).forEach(channel => {
          const touchpoints = (invest.mix[channel] && invest.mix[channel].touchpoints) || {}
          Object.keys(touchpoints).forEach(touchpoint => {
            rows.push([channel, touchpoint, touchpoints[touchpoint]])
          })
        })
        add(t('spendByTouchpoint'), null, rows.length > 1 ? rows : null)
      }

      // Segments
      if (this.showSegmentsTab) {
        const segmentHeaders = [channelHeader, tx('xlsxSegment'), tx('xlsxLabel'), tx('xlsxValue'), t('xlsxVariability')]
        const total = t('xlsxTotal')
        if (this.showSegmentsSpendDetail) {
          const entries = [{ name: total, segments: invest.segments }]
          Object.keys(invest.mix || {}).forEach(channel => {
            if (invest.mix[channel] && invest.mix[channel].segments) entries.push({ name: channel, segments: invest.mix[channel].segments })
          })
          add(t('spendBySegment'), null, segmentDistributionsToRows(entries, segmentHeaders))
        }
        if (this.showSalesByMicroaudience) {
          add(t('microaudienceContribution'), null, namesValuesToRows(output.microaudience, [tx('xlsxName'), kpiHeader]))
        }
        if (output.marketing) {
          const entries = Object.keys(output.marketing)
            .filter(channel => output.marketing[channel] && output.marketing[channel].segments)
            .map(channel => ({ name: channel, segments: output.marketing[channel].segments }))
          add(t('mediaKpiBySegment', { kpi: kpi }), null, segmentDistributionsToRows(entries, segmentHeaders))
        }
      }
      return sheets
    },
    restrictionTypeName: function (type) {
      const types = {
        unrestricted: this.$t('components.SimulationDetailsComponent.unrestricted'),
        more_than: this.$t('components.SimulationDetailsComponent.moreThan'),
        less_than: this.$t('components.SimulationDetailsComponent.lessThan'),
        between: this.$t('components.SimulationDetailsComponent.between'),
        exactly: this.$t('components.SimulationDetailsComponent.exactly')
      }
      return types[type]
    },
    aggregateSalesDecompositionByPeriod (byPeriod, mode = 'sum') {
      if (!byPeriod || typeof byPeriod !== 'object') {
        return { components: [], total: 0 }
      }
      const components = Object.keys(byPeriod).map(name => {
        const ys = (byPeriod[name] && Array.isArray(byPeriod[name].y)) ? byPeriod[name].y : []
        const sum = ys.reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0)
        const value = mode === 'average' && ys.length > 0 ? sum / ys.length : sum
        return { name, value }
      })
      const total = components.reduce((a, c) => a + c.value, 0)
      return { components, total }
    }
  },
  computed: {
    ...mapGetters('auth', [
      'authUser'
    ]),
    ...mapGetters('clients', [
      'currentClientObject'
    ]),
    ngtorchRunsPlans () {
      if (this.shareParams || !this.simulation || typeof this.clientId !== 'string') return []
      return [{ ...this.simulation, clientId: this.clientId }]
    },
    ...mapGetters('simulations', [
      'simulations'
    ]),
    // The tool the plan came from, as the menu names it, so exported files of plans that share a
    // name can still be told apart. Plans without a type predate the Calculator and are optimizations.
    planTypeName () {
      if (this.simulation.budget_simulation_id) return this.$t('views.App.budgetSimulator')
      if (this.simulation.type === 'plan_simulation') return this.$t('views.App.planCalculator')
      return this.$t('views.App.planOptimizer')
    },
    referenceModelConfig () {
      if (this.client.plan_reference_model) {
        return this.client.plan_reference_model.config
      } else {
        return this.client.reference_model.config
      }
    },
    sharePlanStatusText () {
      return this.sharePlanLink && this.sharePlanLink.length > 0 ? this.$t('components.SimulationDetailsComponent.sharingEnabled') : this.$t('components.SimulationDetailsComponent.creatingLink')
    },
    clientId () {
      return this.$route.params.client ? this.$route.params.client : this.shareParams.clientId
    },
    simulationId () {
      return this.$route.params.simulation ? this.$route.params.simulation : this.shareParams.simulationId
    },
    customKpiName () {
      if (this.client.customKpiName) {
        return this.client.customKpiName
      } else {
        return this.$t('components.SimulationDetailsComponent.defaultKpiName')
      }
    },
    showSalesDecompositionPercentages () {
      if (this.client.hideSalesDecompositionPercentages) {
        return false
      } else {
        return true
      }
    },
    prettyMediaNamesDict () {
      return this.client.pretty_names ? this.client.pretty_names.media : {}
    },
    colors () {
      if (this.$route.params.colors) {
        return this.prettyTranslateNames(this.$route.params.colors, [''], this.prettyMediaNamesDict)
      } else if (this.client) {
        return this.prettyTranslateNames(this.client.channelColors, [''], this.prettyMediaNamesDict)
      } else {
        return undefined
      }
    },
    data () {
      if (this.client && this.simulation) {
        const basePaths = [
          'invest.mix',
          'invest.media_spend',
          'weekly_detail.invest.media_spend',
          'sales.marketing',
          'sales.media_attributed_sales_decomposition_by_period',
          'weekly_detail.sales.media_attributed_sales_decomposition_by_period'
        ]
        // salesBasePaths and weeklyDetailSalesBasePaths support outputs, and are prettified recursively later
        const salesBasePaths = [
          'marketing',
          'media_attributed_sales_decomposition_by_period'
        ]
        const weeklyDetailSalesBasePaths = [
          'media_attributed_sales_decomposition_by_period'
        ]
        // Include all outputs (recursive versions of the sales basePaths at sales.outputs.output_name)
        let paths = [].concat(basePaths)
        if (this.simulation.results.sales.outputs) {
          const outputNames = Object.keys(this.simulation.results.sales.outputs)
          if (outputNames && outputNames.length > 0) {
            outputNames.forEach(output => {
              if (output !== 'Total') {
                paths = paths.concat(salesBasePaths.map(path => {
                  return `sales.outputs.${output}.${path}`
                }))
                paths = paths.concat(weeklyDetailSalesBasePaths.map(path => {
                  return `weekly_detail.sales.outputs.${output}.${path}`
                }))
              }
            })
          }
        }
        return this.prettyTranslateNames(this.simulation.results, paths, this.prettyMediaNamesDict)
      } else {
        return {}
      }
    },
    dataInvestTimeArrays: function () {
      const sortedArray = Object.entries(this.data.invest.time).slice().sort((a, b) => new Date(a[0]) - new Date(b[0]))
      let x = []
      let y = []

      sortedArray.forEach(a => {
        x.push(a[0])
        y.push(a[1])
      })
      return {
        x: x,
        y: y
      }
    },
    // The two halves of "can these outputs be added into a Total", kept apart so the Admin tab
    // can name which one is missing.
    outputsCombinableFlag: function () {
      const metadata = this.referenceModelConfig && this.referenceModelConfig.metadata
      return !metadata || metadata.combinable_outputs !== false
    },
    outputsAllAdditive: function () {
      if (!this.client || !this.simulation || !this.data.sales || !this.data.sales.outputs) return true
      const configOutputs = this.referenceModelConfig && this.referenceModelConfig.outputs
      return Object.keys(this.data.sales.outputs).every(name => {
        if (!configOutputs || !configOutputs[name]) return true
        return configOutputs[name].is_additive !== false
      })
    },
    outputs: function () {
      if (this.client && this.simulation && 'outputs' in this.data.sales) {
        // KPIs hidden by the model's Display settings are left out, and so is the Total while
        // any is hidden (it adds up the hidden KPIs too).
        let outputs = {}
        Object.keys(this.data.sales.outputs).forEach(name => {
          if (!isKpiHidden(this.client, name)) {
            outputs[name] = this.data.sales.outputs[name]
          }
        })
        const combinable = this.outputsCombinableFlag && this.outputsAllAdditive
        if ('total' in this.data.sales && combinable && !hasHiddenKpis(this.client)) {
          outputs['Total'] = { ...this.data.sales }
        }
        return outputs
      } else {
        return null
      }
    },
    outputNames: function () {
      if (this.outputs) {
        return Object.keys(this.outputs)
      } else {
        return []
      }
    },
    currentOutputObject: function () {
      const basePaths = [
        'marketing',
        'media_attributed_sales_decomposition_by_period'
      ]
      if (this.currentOutput) {
        return this.prettyTranslateNames(this.outputs[this.currentOutput], basePaths, this.prettyMediaNamesDict)
      } else if ('total' in this.data.sales && !(this.data.sales.outputs && hasHiddenKpis(this.client))) {
        return this.data.sales
      } else {
        const firstKey = firstVisibleOutputName(this.client, this.data.sales.outputs)
        return firstKey ? this.data.sales.outputs[firstKey] : {}
      }
    },
    currentOutputUnitName: function () {
      if (!this.currentOutput || this.currentOutput === 'Total') {
        return this.referenceModelConfig.sales_unit_name || 'USD'
      } else if ('outputs' in this.referenceModelConfig) {
        return this.referenceModelConfig.outputs[this.currentOutput].sales_unit_name || 'USD'
      } else {
        return 'USD'
      }
    },
    currentOutputUnitSymbol: function () {
      if (this.currentOutputUnitName === 'USD') {
        return '$'
      } else {
        return ''
      }
    },
    spendUnitName: function () {
      if ('invest_unit_name' in this.referenceModelConfig) {
        return this.referenceModelConfig.invest_unit_name
      } else {
        return 'USD'
      }
    },
    budgetDisplay: function () {
      // For spend_up_to beta plans, simulation.budget is just a ceiling; show the
      // real invested amount as the main figure and the ceiling as a caption.
      const invested = investedBudget(this.simulation)
      if (isSpendUpTo(this.simulation) && invested !== null) {
        return {
          value: invested,
          caption: this.$t('components.SimulationDetailsComponent.upTo', { value: this.formatValue(this.simulation.budget) })
        }
      }
      return { value: this.simulation.budget, caption: '' }
    },
    salesDecompositionAggregationMode () {
      const configOutputs = this.referenceModelConfig && this.referenceModelConfig.outputs
      const isOutputAdditive = (outputName) => {
        if (!configOutputs || !configOutputs[outputName]) return true
        return configOutputs[outputName].is_additive !== false
      }
      const isTotal = !this.currentOutput || this.currentOutput === 'Total'
      if (!isTotal) {
        return isOutputAdditive(this.currentOutput) ? 'sum' : 'average'
      }
      const realOutputs = (this.data && this.data.sales && this.data.sales.outputs)
        ? Object.keys(this.data.sales.outputs)
        : []
      const allAdditive = realOutputs.every(name => isOutputAdditive(name))
      return allAdditive ? 'sum' : 'average'
    },
    salesDecompositionByPeriodData () {
      const obj = this.currentOutputObject
      if (!obj || !obj.sales_decomposition_by_period) {
        return null
      }
      const aggregated = this.aggregateSalesDecompositionByPeriod(obj.sales_decomposition_by_period, this.salesDecompositionAggregationMode)
      // Canonical display order: prefer sales_decomposition_total.components when present as an array (Model Report parity).
      // Otherwise fall back to descending-abs-value, which matches the legacy SalesDecomposition chart behavior on plans —
      // currentOutputObject.components is a plain object on the plan side (see simulationDetails/MarketingSalesDetail.vue),
      // so the legacy chart hit SalesDecomposition.vue's "Old versions" branch and sorted by Math.abs(value) descending.
      // sales_decomposition_by_period's own key order isn't an API contract, so without one of these we'd get arbitrary order.
      let ordered = null
      if (obj.sales_decomposition_total && Array.isArray(obj.sales_decomposition_total.components)) {
        const totalComponents = obj.sales_decomposition_total.components
        const valueByName = {}
        aggregated.components.forEach(c => { valueByName[c.name] = c.value })
        ordered = []
        const seen = {}
        totalComponents.forEach(c => {
          if (c.name in valueByName) {
            ordered.push({ name: c.name, value: valueByName[c.name] })
            seen[c.name] = true
          } else {
            console.log(`Warning: sales decomposition component '${c.name}' present in sales_decomposition_total.components but missing from sales_decomposition_by_period`)
          }
        })
        aggregated.components.forEach(c => {
          if (!seen[c.name]) {
            console.log(`Warning: sales decomposition component '${c.name}' present in sales_decomposition_by_period but missing from sales_decomposition_total.components`)
            ordered.push(c)
          }
        })
      } else {
        ordered = aggregated.components.slice().sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
      }
      return { components: ordered, total: aggregated.total }
    },
    mediaAttributedDecompositionByPeriodData () {
      const obj = this.currentOutputObject
      if (!obj || !obj.media_attributed_sales_decomposition_by_period) {
        return null
      }
      const aggregated = this.aggregateSalesDecompositionByPeriod(
        obj.media_attributed_sales_decomposition_by_period,
        this.salesDecompositionAggregationMode
      )
      const marketing = {}
      aggregated.components.forEach(c => {
        marketing[c.name] = { adstock: c.value }
      })
      // Surface backend drift between the by_period series and the canonical totals
      // object the rest of the report uses (Model Report / SimulationDetails parity).
      if (obj.media_attributed_sales_decomposition_total &&
          Array.isArray(obj.media_attributed_sales_decomposition_total.components)) {
        const inByPeriod = new Set(aggregated.components.map(c => c.name))
        obj.media_attributed_sales_decomposition_total.components.forEach(c => {
          if (!inByPeriod.has(c.name)) {
            console.log(`Warning: media component '${c.name}' present in media_attributed_sales_decomposition_total.components but missing from media_attributed_sales_decomposition_by_period`)
          }
        })
        const inTotal = new Set(obj.media_attributed_sales_decomposition_total.components.map(c => c.name))
        aggregated.components.forEach(c => {
          if (!inTotal.has(c.name)) {
            console.log(`Warning: media component '${c.name}' present in media_attributed_sales_decomposition_by_period but missing from media_attributed_sales_decomposition_total.components`)
          }
        })
      }
      return { marketing }
    },
    sales () {
      if (this.salesDecompositionByPeriodData) {
        return this.salesDecompositionByPeriodData.total
      }
      return this.currentOutputObject ? this.currentOutputObject.total : 0
    },
    modelReportFetchKey () {
      // Stable identity of the Storage-backed report version, so the watcher refetches
      // whenever the server uploads a new file (mirrors ModelReportComponent).
      if (!this.client || !this.client.model_report_storage) return null
      const ts = this.client.model_report_updated_at
      let tsKey = ''
      if (ts) {
        if (typeof ts.toMillis === 'function') tsKey = String(ts.toMillis())
        else if (ts.seconds !== undefined) tsKey = ts.seconds + '.' + (ts.nanoseconds || 0)
        else tsKey = String(ts)
      }
      return this.client.model_report_storage + '@' + tsKey
    },
    parsedInlineModelReport () {
      // Legacy inline report (string or object) for clients without Storage-backed reports.
      if (!this.client || this.client.model_report_storage || !this.client.model_report) return null
      if (typeof this.client.model_report === 'string') {
        try {
          return JSON.parse(this.client.model_report)
        } catch (e) {
          console.error('Failed to parse model_report:', e)
          return null
        }
      }
      return this.client.model_report
    },
    // The model report backing the saturation curves: from Storage when the client has one,
    // otherwise the legacy inline field.
    planModelReport () {
      if (!this.client) return null
      return this.client.model_report_storage ? this.fetchedModelReport : this.parsedInlineModelReport
    },
    // The saturation object for the currently selected output, or null.
    planReportSaturation () {
      const report = this.planModelReport
      if (!report) return null
      if (report.outputs && this.currentOutput && this.currentOutput in report.outputs && report.outputs[this.currentOutput].saturation) {
        return report.outputs[this.currentOutput].saturation
      }
      if (report.saturation) {
        return report.saturation
      }
      if (report.outputs) {
        const firstOutput = Object.keys(report.outputs).find(name => !isKpiHidden(this.client, name) && report.outputs[name].saturation)
        return firstOutput ? report.outputs[firstOutput].saturation : null
      }
      return null
    },
    // The per-channel per-period spend series matching the curve's granularity, or null when
    // the plan has none to overlay (the curves still draw, just without datapoints).
    planSpendSeries () {
      if (!this.client || !this.simulation) return null
      const granularity = this.client.reference_model && this.client.reference_model.config
        ? this.client.reference_model.config.saturation_granularity
        : null
      if (granularity === 'W' && this.data.weekly_detail && this.data.weekly_detail.invest && this.data.weekly_detail.invest.media_spend) {
        return this.data.weekly_detail.invest.media_spend
      }
      if (this.data.invest && this.data.invest.media_spend) {
        return this.data.invest.media_spend
      }
      return null
    },
    // Per-channel saturation curves from the model report, with this plan's spend
    // overlaid as datapoints (one point per period, sitting on the curve). null when
    // there's no report or no saturation data.
    planSaturationData () {
      if (!this.client || !this.simulation) return null
      const saturation = this.planReportSaturation
      if (!saturation) return null
      const spendSeries = this.planSpendSeries
      // Overlay the plan's spend on one flat { channel: curve } object.
      const overlaySpend = (channels) => {
        const result = {}
        Object.keys(channels).forEach(channel => {
          const curve = channels[channel]
          const series = spendSeries ? spendSeries[channel] : null
          if (curve && curve.x && series && Array.isArray(series.y)) {
            const datapoints = series.y.map(spend => ({
              x: spend,
              y: interpolateCurveValue(curve.x, curve.y, spend)
            }))
            result[channel] = {
              x: curve.x,
              y: curve.y,
              regions: curve.regions,
              datapoints: datapoints,
              datapoints_dates: series.x
            }
          } else {
            // No spend to overlay for this channel — keep the bare curve.
            result[channel] = curve
          }
        })
        return result
      }
      // Grouped saturation (first-level 'Instant Marketing' sub-key, same detection as the
      // Model Report): translate and overlay the channels inside each group, keeping the
      // group level so SaturationCurves' group selector drives it.
      if (Object.prototype.hasOwnProperty.call(saturation, 'Instant Marketing')) {
        const out = {}
        for (const group in saturation) {
          if (Object.prototype.hasOwnProperty.call(saturation, group)) {
            out[group] = overlaySpend(this.prettyTranslateNames(saturation[group], [''], this.prettyMediaNamesDict))
          }
        }
        return out
      }
      // Translate channel keys so they line up with `colors` and the plan's spend series.
      return overlaySpend(this.prettyTranslateNames(saturation, [''], this.prettyMediaNamesDict))
    },
    // Composite unit of the per-channel ROAS, empty when the selected output and the
    // spend share a unit and the ratio is dimensionless.
    roasUnit () {
      return roasUnitLabel(this.currentOutputUnitName, this.spendUnitName) || ''
    },
    roasByChannelExportName () {
      const base = `${this.client.name} - ${this.simulation.name} - ROAS by Channel`
      return this.roasUnit ? `${base} (${this.roasUnit})` : base
    },
    // Per-channel ROAS (mean + lower/upper confidence bounds) from the plan's results
    // JSON (root-level roas_by_channel key). null hides the section — only when the
    // plan carries no such data, since the ratio itself is always meaningful.
    roasByChannelData () {
      if (!this.simulation || !this.simulation.results) return null
      const roas = this.simulation.results.roas_by_channel
      if (!roas || Object.keys(roas).length === 0) return null
      return this.prettyTranslateNames(roas, [''], this.prettyMediaNamesDict)
    },
    // -------------------------------------------------------------------------------------
    // Section visibility. Every v-if in the template reads one of these, and hiddenSections
    // below negates these same computeds, so the Admin tab's list cannot drift from what is
    // actually rendered.
    // -------------------------------------------------------------------------------------
    investData () {
      return (this.data && this.data.invest) ? this.data.invest : {}
    },
    showRestrictionsChip () {
      return !!(this.simulation && this.simulation.restrictions && Object.keys(this.simulation.restrictions).length > 0)
    },
    showSalesPrecisionChip () {
      return !!(this.data.precision && this.data.precision.sales)
    },
    showMonthlyDetailTab () {
      return !!(this.investData.media_spend && this.investData.time && Object.keys(this.investData.time).length > 1)
    },
    showWeeklyDetailTab () {
      const weekly = this.data ? this.data.weekly_detail : null
      return !!(weekly && weekly.sales && weekly.sales.time && Object.keys(weekly.sales.time).length > 1)
    },
    showTouchpointsTab () {
      const mix = this.investData.mix
      const first = mix ? Object.keys(mix)[0] : null
      return !!(first && mix[first].touchpoints)
    },
    showSegmentsTab () {
      return !!this.investData.segments
    },
    showAdminTab () {
      return this.authUser.isAdmin || this.authUser.isScientist
    },
    showRoasByChannel () {
      return !!this.roasByChannelData
    },
    showSaturationCurves () {
      return !!this.planSaturationData
    },
    canExportSaturationCurves () {
      return this.authUser.isAdmin || this.authUser.isScientist
    },
    showSegmentsSpendDetail () {
      return !!this.investData.segments
    },
    showSalesByMicroaudience () {
      return !!(this.currentOutputObject && this.currentOutputObject.microaudience)
    },
    // Everything this view decided not to render, with the reason and the condition that failed.
    // Built by negating the show* computeds the template itself uses, never by re-deriving the
    // rules. A hidden tab contributes one row for the tab instead of one per chart inside it.
    hiddenSections () {
      const list = []
      if (!this.client || !this.simulation) return list
      const t = (key, params) => this.$t('components.SimulationDetailsComponent.' + key, params || {})
      const add = (group, name, reason, condition, params) => {
        list.push({ group: group, name: name, reason: reason, condition: condition, params: params || {} })
      }
      const header = t('hiddenGroupHeader')
      const tabsGroup = t('hiddenGroupTabs')
      const summary = t('fullPeriodSummary')
      const saturationGroup = this.$t('components.SaturationCurves.saturationCurves')

      // --- Header ---
      if (!this.outputs) {
        add(header, t('kpiSelector'), 'missingData', 'results.sales.outputs')
      } else if (!('total' in this.data.sales)) {
        add(header, t('totalKpiOption'), 'missingData', 'results.sales.total')
      } else if (!this.outputsCombinableFlag) {
        add(header, t('totalKpiOption'), 'clientSetting', 'reference_model.config.metadata.combinable_outputs === false')
      } else if (!this.outputsAllAdditive) {
        add(header, t('totalKpiOption'), 'notAdditive', 'reference_model.config.outputs[*].is_additive === false')
      } else if (hasHiddenKpis(this.client)) {
        add(header, t('totalKpiOption'), 'clientSetting', 'client.hidden_kpis')
      }
      if (this.outputs) {
        hiddenKpis(this.client).forEach(name => {
          if (name in this.data.sales.outputs) {
            add(header, t('kpiOption', { kpi: name }), 'clientSetting', 'client.hidden_kpis')
          }
        })
      }
      if (!this.showRestrictionsChip) {
        add(header, t('restrictionsChip'), 'missingData', 'simulation.restrictions')
      }
      if (!this.showSalesPrecisionChip) {
        add(header, t('precisionChip'), 'missingData', 'results.precision.sales')
      }

      // --- Tabs ---
      if (!this.showMonthlyDetailTab) {
        const noSeries = !this.investData.media_spend || !this.investData.time
        add(tabsGroup, t('monthlyDetailView'), noSeries ? 'missingData' : 'notEnoughPeriods',
          noSeries ? 'invest.media_spend / invest.time' : 'invest.time ≤ 1')
      }
      if (!this.showWeeklyDetailTab) {
        const noWeekly = !this.data.weekly_detail
        add(tabsGroup, t('weeklyDetailView'), noWeekly ? 'missingData' : 'notEnoughPeriods',
          noWeekly ? 'weekly_detail' : 'weekly_detail.sales.time ≤ 1')
      }
      if (!this.showTouchpointsTab) {
        add(tabsGroup, t('touchpoints'), 'missingData', 'invest.mix[channel].touchpoints')
      }
      if (!this.showSegmentsTab) {
        add(tabsGroup, t('segments'), 'missingData', 'invest.segments')
      }

      // --- Full Period Summary ---
      if (!this.showRoasByChannel) {
        add(summary, t('roasByChannel'), 'missingData', 'results.roas_by_channel')
      }
      if (!this.showSaturationCurves) {
        const noReport = !this.planModelReport
        add(summary, saturationGroup, noReport ? 'noModelReport' : 'missingData',
          noReport ? 'client.model_report' : 'model_report.saturation')
      } else if (!this.planSpendSeries) {
        add(summary, t('planSpendDatapoints'), 'noSpendSeries', 'invest.media_spend')
      }

      // --- Saturation Curves internals, only when the section itself renders ---
      if (this.showSaturationCurves) {
        hiddenSaturationSections(this.planSaturationData, {
          enableGroupSelector: true,
          enableImpressionsModel: this.client.enableImpressionsModel,
          canExportCurves: this.canExportSaturationCurves
        }).forEach(row => {
          add(saturationGroup, this.$t(row.nameKey), row.reason, row.condition, row.params)
        })
      }

      // --- Per-period charts, one block per detail tab that is reachable ---
      const periodicName = (key) => {
        if (key === 'budgetAllocation') return this.$t('components.PeriodicDetail.budgetAllocationAndMediaMix')
        if (key === 'kpiByPeriod') return this.customKpiName
        if (key === 'salesDecomposition') return this.$t('components.PeriodicDetail.salesDecomposition', { kpi: this.customKpiName })
        return this.$t('components.PeriodicDetail.' + key)
      }
      const addPeriodic = (group, data) => {
        const visibility = periodicDetailVisibility(data, this.currentOutput)
        PERIODIC_DETAIL_SECTIONS.forEach(section => {
          if (!visibility[section.key]) {
            add(group, periodicName(section.key), 'missingData', section.dataKey)
          }
        })
      }
      if (this.showMonthlyDetailTab) {
        addPeriodic(t('monthlyDetailView'), this.data)
      }
      if (this.showWeeklyDetailTab) {
        addPeriodic(t('weeklyDetailView'), this.data.weekly_detail)
      }

      // --- Segments, only when the tab itself is reachable ---
      if (this.showSegmentsTab) {
        if (!this.showSegmentsSpendDetail) {
          add(t('segments'), t('segmentsSpendDetail'), 'missingData', 'invest.segments')
        }
        if (!this.showSalesByMicroaudience) {
          add(t('segments'), t('salesByMicroaudience', { kpi: this.customKpiName }), 'missingData', 'sales.microaudience')
        }
      }
      return list
    }
  },
  watch: {
    modelReportFetchKey: {
      immediate: true,
      handler: function (newKey) {
        if (!newKey) {
          this.fetchedModelReport = null
          this.fetchedModelReportFor = null
          return
        }
        if (newKey === this.fetchedModelReportFor) return
        const target = newKey
        loadModelReportFromClient(this.client)
          .then(report => {
            if (this.modelReportFetchKey === target) {
              this.fetchedModelReport = report
              this.fetchedModelReportFor = target
            }
          })
          .catch(err => {
            console.error('Failed to load model_report from Storage:', err)
            if (this.modelReportFetchKey === target) {
              this.fetchedModelReport = null
              this.fetchedModelReportFor = target
            }
          })
      }
    },
    currentClientObject: function (newVal) {
      if (typeof newVal === 'object') {
        this.client = newVal
      }
    },
    simulations: {
      handler: function (newVal) {
        if (Array.isArray(newVal) && typeof this.simulationId === 'string') {
          let simulation = newVal.find(obj => obj.id === this.simulationId)
          // Support stringified results, for supporting very large results objects (Firebase chokes on them and deletes the full simulation)
          if (simulation && simulation.results && typeof simulation.results === 'string') {
            simulation.results = JSON.parse(simulation.results)
          }
          this.simulation = simulation
        }
      },
      immediate: true
    },
    '$route.params': function (newVal) {
      this.loadSimulations({
        client: this.$route.params.client
      })
      this.setCurrentClient(this.$route.params.client)
    },
    shareParams: function () {
      if (this.shareParams && this.shareParams.clientObject) {
        this.simulation = this.shareParams.simulationObject
        this.setCurrentClient(this.shareParams.clientObject)
      }
    },
    outputNames: function () {
      // Also re-pick when the selected KPI is no longer offered (it was just hidden).
      if (this.outputNames && (!this.currentOutput || !this.outputNames.includes(this.currentOutput))) {
        if (this.outputNames.includes('Total')) {
          this.currentOutput = 'Total'
        } else {
          this.currentOutput = this.outputNames[0]
        }
      }
    }
  },
  async created () {
    const simulation = this.simulationId
    const client = this.clientId
    // Normal authenticated simulation view
    if (!this.shareParams) {
      if (typeof simulation === 'object') {
        this.simulation = simulation
      } else {
        if (typeof client === 'string') {
          this.loadSimulations({
            client: this.clientId
          })
        } else if (typeof client === 'object' && this.$route.params.client.id) {
          this.loadSimulations({
            client: this.$route.params.client.id
          })
        } else {
          console.error('Something is wrong with client and simulation route params')
        }
      }
      if (typeof client === 'object') {
        this.client = client
      } else {
        this.setCurrentClient(client)
      }
    }
    // Always do this
    if (this.outputNames && !this.currentOutput) {
      if (this.outputNames.includes('Total')) {
        this.currentOutput = 'Total'
      } else {
        this.currentOutput = this.outputNames[0]
      }
    }
  },
  props: {
    shareParams: {
      type: Object,
      required: false
    }
  },
  components: {
    TextBoxMetric,
    MarketingSpend,
    MarketingSpendDetail,
    SalesDecomposition,
    MarketingSalesByMicroaudience,
    MarketingSales,
    MarketingSalesDetail,
    MarketingSpendSegmentsDetail,
    PercentChip,
    RoasByChannel,
    SaturationCurves,
    BarChart,
    PeriodicDetail,
    HiddenSectionsReport,
    NgtorchRunChip
  }
}
